import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  CreditCard,
  CheckCircle,
  XCircle,
  SpinnerGap,
  Warning,
  CalendarBlank,
  ClockCountdown,
  ShieldCheck,
} from '@phosphor-icons/react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const daysBetween = (future) => {
  if (!future) return null;
  const diffMs = new Date(future).getTime() - Date.now();
  return Math.max(0, Math.ceil(diffMs / 86400000));
};

export const SubscriptionPage = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [subData, setSubData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [renewing, setRenewing] = useState(false);

  // Welcome-back banner after a successful payment redirects here
  const [welcomeBack, setWelcomeBack] = useState(false);

  const fetchSubscription = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/payments/subscription-status`);
      setSubData(data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  // Detect ?welcome=1 query param (set by PaymentSuccessPage after a successful checkout)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('welcome') === '1') {
      setWelcomeBack(true);
      refreshUser();
      fetchSubscription();
      const t = setTimeout(() => setWelcomeBack(false), 8000);
      return () => clearTimeout(t);
    }
  }, [location.search, fetchSubscription, refreshUser]);

  const handleRenew = async () => {
    setRenewing(true);
    try {
      const originUrl = window.location.origin;
      const { data } = await axios.post(
        `${API}/api/payments/renew-subscription`,
        { origin_url: originUrl },
      );
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Renewal error:', error);
    } finally {
      setRenewing(false);
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await axios.post(`${API}/api/payments/cancel-subscription`, {});
      await refreshUser();
      await fetchSubscription();
      setCancelDialogOpen(false);
    } catch (error) {
      console.error('Cancel error:', error);
    } finally {
      setCancelling(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'cancelled': return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200';
      default: return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <SpinnerGap weight="bold" className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  const status = subData?.subscription_status || user?.subscription_status || 'inactive';
  const expires = subData?.subscription_expires || user?.subscription_expires;
  const daysRemaining = expires ? daysBetween(expires) : null;
  const hasGracePeriod = status === 'cancelled' && daysRemaining !== null && daysRemaining > 0;
  const isLapsed =
    status === 'inactive' ||
    (status === 'cancelled' && daysRemaining !== null && daysRemaining <= 0);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-8">Subscription</h1>

        {/* Welcome-back banner after successful checkout */}
        {welcomeBack && status === 'active' && (
          <div
            className="mb-6 border border-green-200 bg-green-50 dark:bg-green-900/20 dark:border-green-800 p-4 flex items-start gap-3 animate-fade-in-up"
            data-testid="welcome-back-banner"
          >
            <CheckCircle weight="fill" className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-green-800 dark:text-green-300">Welcome back!</p>
              <p className="text-sm text-green-700 dark:text-green-400">
                Your subscription is active again. You now have full access to the sanctions database.
              </p>
            </div>
          </div>
        )}

        {/* Prominent lapsed-state hero card */}
        {isLapsed && (
          <div
            className="mb-6 border border-destructive/30 bg-destructive/5 p-6 sm:p-8"
            data-testid="lapsed-state-card"
          >
            <div className="flex items-start gap-4">
              <Warning weight="duotone" className="h-10 w-10 text-destructive flex-shrink-0" />
              <div className="flex-1">
                <h2 className="text-xl font-bold tracking-tight mb-1">
                  {status === 'cancelled'
                    ? 'Your subscription has ended'
                    : 'Subscription inactive'}
                </h2>
                <p className="text-sm text-muted-foreground mb-4">
                  {status === 'cancelled' && expires
                    ? `Your access ended on ${formatDate(expires)}. Resubscribe to regain uninterrupted access to the sanctions database.`
                    : 'Subscribe now to unlock the full DVSA sanctions database, offence detail pages, and saved offences.'}
                </p>
                <Button
                  onClick={handleRenew}
                  disabled={renewing}
                  className="rounded-sm"
                  data-testid="resubscribe-hero-btn"
                >
                  {renewing ? (
                    <><SpinnerGap className="mr-2 h-4 w-4 animate-spin" /> Redirecting to Stripe...</>
                  ) : (
                    <><CreditCard weight="duotone" className="mr-2 h-4 w-4" /> Resubscribe — £0.99/month</>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Grace period countdown card */}
        {hasGracePeriod && (
          <div
            className="mb-6 border border-amber-300/60 bg-amber-50 dark:bg-amber-900/15 dark:border-amber-800 p-6"
            data-testid="grace-period-card"
          >
            <div className="flex items-start gap-4">
              <ClockCountdown weight="duotone" className="h-10 w-10 text-amber-600 flex-shrink-0" />
              <div className="flex-1">
                <h2 className="text-lg font-bold tracking-tight mb-1">
                  {daysRemaining} day{daysRemaining === 1 ? '' : 's'} of access remaining
                </h2>
                <p className="text-sm text-muted-foreground mb-4">
                  Your subscription was cancelled but you still have access until <strong>{formatDate(expires)}</strong>. Reactivate before then to avoid any gap in service.
                </p>
                <Button
                  onClick={handleRenew}
                  disabled={renewing}
                  className="rounded-sm"
                  data-testid="reactivate-btn"
                >
                  {renewing ? (
                    <><SpinnerGap className="mr-2 h-4 w-4 animate-spin" /> Redirecting...</>
                  ) : (
                    <><CreditCard weight="duotone" className="mr-2 h-4 w-4" /> Reactivate — £0.99/month</>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Status Card */}
        <div className="border border-border mb-8">
          <div className="p-6 border-b border-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] font-medium text-muted-foreground mb-2">Status</p>
                <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(status)}`} data-testid="subscription-status">
                  {status === 'active' ? 'Active' : status === 'cancelled' ? 'Cancelled' : 'Inactive'}
                </span>
              </div>
              <div className="text-right">
                <p className="text-xs uppercase tracking-[0.2em] font-medium text-muted-foreground mb-2">Price</p>
                <p className="text-2xl font-bold">£0.99<span className="text-sm text-muted-foreground font-normal">/month</span></p>
              </div>
            </div>
          </div>

          {expires && (
            <div className="p-6 border-b border-border flex items-center gap-3">
              <CalendarBlank weight="duotone" className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">
                  {status === 'cancelled' ? 'Access until' : 'Renews on'}
                </p>
                <p className="text-sm text-muted-foreground">{formatDate(expires)}</p>
              </div>
            </div>
          )}

          <div className="p-6 flex flex-col sm:flex-row gap-3">
            {status === 'active' && (
              <Button
                variant="outline"
                onClick={() => setCancelDialogOpen(true)}
                className="rounded-sm"
                data-testid="cancel-subscription-btn"
              >
                <XCircle weight="duotone" className="mr-2 h-4 w-4" />
                Cancel Subscription
              </Button>
            )}
            {/* Small secondary resubscribe/subscribe button stays available but hero card handles the main CTA */}
            {!isLapsed && !hasGracePeriod && status !== 'active' && (
              <Button
                onClick={handleRenew}
                disabled={renewing}
                className="rounded-sm"
                data-testid="renew-subscription-btn"
              >
                {renewing ? (
                  <><SpinnerGap className="mr-2 h-4 w-4 animate-spin" /> Redirecting...</>
                ) : (
                  <><CreditCard weight="duotone" className="mr-2 h-4 w-4" /> Subscribe — £0.99/month</>
                )}
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="rounded-sm"
            >
              Back to Dashboard
            </Button>
          </div>
        </div>

        {/* What you get (only show when lapsed to re-hook the user) */}
        {isLapsed && (
          <div className="border border-border mb-8 p-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <ShieldCheck weight="duotone" className="h-5 w-5 text-primary" />
              What you'll get back
            </h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2"><CheckCircle weight="fill" className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" /> Full DVSA sanctions database (updated for May 2026 policy)</li>
              <li className="flex items-start gap-2"><CheckCircle weight="fill" className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" /> Search & filter by vehicle type, severity, category</li>
              <li className="flex items-start gap-2"><CheckCircle weight="fill" className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" /> Offence detail pages with likely outcomes</li>
              <li className="flex items-start gap-2"><CheckCircle weight="fill" className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" /> Save offences for quick reference</li>
              <li className="flex items-start gap-2"><CheckCircle weight="fill" className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" /> Cancel any time</li>
            </ul>
          </div>
        )}

        {/* Payment History */}
        {subData?.payment_history?.length > 0 && (
          <div className="border border-border">
            <div className="p-4 border-b border-border">
              <h2 className="font-semibold">Payment History</h2>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead className="text-right">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {subData.payment_history.map((tx, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-sm">{formatDate(tx.created_at)}</TableCell>
                    <TableCell className="text-sm font-medium">£{tx.amount?.toFixed(2)}</TableCell>
                    <TableCell className="text-right">
                      <Badge variant="secondary" className="rounded-sm">Paid</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </main>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Subscription</DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground">
            Are you sure you want to cancel your subscription? You'll retain access until <strong>{formatDate(expires)}</strong>.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialogOpen(false)} className="rounded-sm">
              Keep Subscription
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancel}
              disabled={cancelling}
              className="rounded-sm"
              data-testid="confirm-cancel-btn"
            >
              {cancelling ? 'Cancelling...' : 'Yes, Cancel'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
